import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ShieldAlert, AlertTriangle, Info, X } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useRBAC } from '../hooks/useRBAC';
import { FUSION_PATH, fusionAlertSummary, isFusionAlert } from './fusion/fusionAlerts';

const POLL_INTERVAL_MS = 60 * 1000; // 60 seconds

const severityConfig = {
  CRITICAL: { color: 'text-red-500',    bg: 'bg-red-50 border-red-200',    dot: 'bg-red-500',    icon: ShieldAlert },
  HIGH:     { color: 'text-orange-500', bg: 'bg-orange-50 border-orange-200', dot: 'bg-orange-500', icon: AlertTriangle },
  MEDIUM:   { color: 'text-yellow-500', bg: 'bg-yellow-50 border-yellow-200', dot: 'bg-yellow-500', icon: Info },
  LOW:      { color: 'text-blue-500',   bg: 'bg-blue-50 border-blue-200',   dot: 'bg-blue-500',   icon: Info },
};

export const GrcAlertBell = ({ orgId }) => {
  const [alerts, setAlerts]     = useState([]);
  const [open, setOpen]         = useState(false);
  const panelRef                = useRef(null);
  const intervalRef             = useRef(null);
  const apiCall                 = useApi();
  const navigate                = useNavigate();
  const { canManageSecurityInsights } = useRBAC();
  // Read-only viewers (incl. super/support admins) see alerts but can't mark them read.
  const canManage               = canManageSecurityInsights(orgId);

  const fetchAlerts = useCallback(async () => {
    if (!orgId) return;
    try {
      // suppressAuthRedirect=true: the bell is a background poll — a 401/403 here must not bounce
      // the whole app through OIDC / the org picker. Fail quietly (the catch below ignores it).
      const data = await apiCall('GET', '/grc-alerts', null, false, null, true);
      setAlerts(Array.isArray(data) ? data : []);
    } catch {
      // silently ignore — bell should not block navigation on API error
    }
  }, [orgId, apiCall]);

  useEffect(() => {
    if (!orgId) { setAlerts([]); return; }
    fetchAlerts();
    intervalRef.current = setInterval(fetchAlerts, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close panel when clicking outside
  useEffect(() => {
    const handleOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const unreadCount = alerts.filter(a => !a.isRead).length;

  const markAsRead = async (id) => {
    try {
      await apiCall('PUT', `/grc-alerts/${id}/read`);
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, isRead: true } : a));
    } catch { /* ignore */ }
  };

  const markAllAsRead = async () => {
    try {
      await apiCall('PUT', '/grc-alerts/read-all');
      setAlerts(prev => prev.map(a => ({ ...a, isRead: true })));
    } catch { /* ignore */ }
  };

  const handleAlertClick = async (alert) => {
    await markAsRead(alert.id);
    setOpen(false);
    // A Fusion regression alert is about a tracked chain, so it opens Fusion, not the control list.
    if (isFusionAlert(alert)) navigate(FUSION_PATH);
    else navigate('/SecurityInsights', { state: { tab: 'grc-alerts' } });
  };

  const formatTime = (dateStr) => {
    // Backend returns UTC datetimes without a 'Z' suffix; ensure JS treats them as UTC
    // (otherwise new Date() parses them as local time, producing negative "Xs ago").
    const s = String(dateStr);
    const d = new Date(s.endsWith('Z') || s.includes('+') || s.includes('-', 10) ? s : s + 'Z');
    const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (diff < 60)  return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(prev => !prev)}
        className="relative p-2 rounded-lg text-content-secondary hover:text-content-primary hover:bg-theme-hover transition-colors"
        title="GRC Alerts"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-theme-secondary rounded-lg shadow-theme-lg border border-border z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold text-content-primary">GRC Alerts</span>
            <div className="flex items-center gap-2">
              {canManage && unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-accent-cyan hover:underline"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-content-muted hover:text-content-primary">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Alert list */}
          <div className="max-h-80 overflow-y-auto">
            {alerts.length === 0 ? (
              <p className="px-4 py-6 text-sm text-content-muted text-center">No GRC alerts</p>
            ) : (
              alerts.map(alert => {
                const cfg = severityConfig[alert.severity] ?? severityConfig.LOW;
                const SeverityIcon = cfg.icon;
                return (
                  <button
                    key={alert.id}
                    onClick={() => handleAlertClick(alert)}
                    className={`w-full text-left px-4 py-3 border-b border-border last:border-b-0 hover:bg-theme-hover transition-colors ${!alert.isRead ? 'bg-theme-hover/50' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5 flex-shrink-0">
                        <SeverityIcon className={`h-4 w-4 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className={`text-[10px] font-bold uppercase tracking-wide ${cfg.color}`}>
                            {alert.severity}
                          </span>
                          {!alert.isRead && (
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                          )}
                        </div>
                        <p className="text-xs font-medium text-content-primary truncate">
                          {alert.controlTitle}
                        </p>
                        <p className="text-xs text-content-muted mt-0.5">
                          {isFusionAlert(alert) ? fusionAlertSummary(alert) : <>{alert.previousStatus} → {alert.newStatus}</>}
                        </p>
                        <p className="text-[10px] text-content-muted mt-1">{formatTime(alert.changedAt)}</p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {alerts.length > 0 && (
            <div className="px-4 py-2 border-t border-border">
              <button
                onClick={() => { setOpen(false); navigate('/SecurityInsights', { state: { tab: 'grc-alerts' } }); }}
                className="text-xs text-accent-cyan hover:underline w-full text-center"
              >
                View all in GRC Alerts →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
